import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Filter, X, Trash2, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Tickets = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [deletedTickets, setDeletedTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pickupDateFilter, setPickupDateFilter] = useState('');
  const [deliveryDateFilter, setDeliveryDateFilter] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTickets();
    fetchDeletedTickets();
  }, []);

  useEffect(() => {
    let filtered = tickets;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter((ticket) => ticket.status === statusFilter);
    }
    
    if (pickupDateFilter) {
      filtered = filtered.filter((ticket) => {
        if (!ticket.pickup_date) return false;
        const ticketPickupDate = new Date(ticket.pickup_date).toISOString().split('T')[0];
        return ticketPickupDate === pickupDateFilter;
      });
    }
    
    if (deliveryDateFilter) {
      filtered = filtered.filter((ticket) => {
        if (!ticket.delivery_date) return false;
        const ticketDeliveryDate = new Date(ticket.delivery_date).toISOString().split('T')[0];
        return ticketDeliveryDate === deliveryDateFilter;
      });
    }
    
    if (searchTerm) {
      filtered = filtered.filter(
        (ticket) =>
          ticket.ticket_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ticket.customer_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredTickets(filtered);
  }, [searchTerm, statusFilter, pickupDateFilter, deliveryDateFilter, tickets]);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data);
      setFilteredTickets(response.data);
    } catch (error) {
      toast.error('Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets?include_deleted=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletedTickets(response.data);
    } catch (error) {
      toast.error('Failed to fetch deleted tickets');
    }
  };

  const handleDeleteTicket = async (ticketId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this ticket? It will be moved to Deleted Tickets.')) return;
    
    try {
      await axios.delete(`${API}/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket deleted successfully');
      fetchTickets();
      fetchDeletedTickets();
    } catch (error) {
      toast.error('Failed to delete ticket');
    }
  };

  const handleRestoreTicket = async (ticketId, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/tickets/${ticketId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket restored successfully');
      fetchTickets();
      fetchDeletedTickets();
    } catch (error) {
      toast.error('Failed to restore ticket');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      picked: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
      washed: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
      ready: 'bg-green-100 text-green-700 hover:bg-green-200',
      delivered: 'bg-slate-100 text-slate-700 hover:bg-slate-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const renderActiveTickets = () => {
    if (filteredTickets.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-slate-400 mb-4">
              <svg className="w-20 h-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-slate-500 text-lg mb-2">No tickets found</p>
            <p className="text-slate-400 text-sm">Create your first ticket to get started</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {filteredTickets.map((ticket) => (
          <Card
            key={ticket.id}
            className="card-hover cursor-pointer"
            onClick={() => navigate(`/tickets/${ticket.id}`)}
            data-testid={`ticket-card-${ticket.id}`}
          >
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-mono text-lg font-semibold text-slate-900">
                      {ticket.ticket_number}
                    </h3>
                    <Badge className={`${getStatusColor(ticket.status)}`}>
                      {ticket.status}
                    </Badge>
                  </div>
                  <p className="text-slate-600 mb-1">{ticket.customer_name}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                    <span>Service: {ticket.service_type}</span>
                    <span>•</span>
                    <span>{ticket.total_kg || ticket.kg || 0} kg</span>
                    <span>•</span>
                    <span>{ticket.clothes_count || ticket.items?.length || 0} items</span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Pickup Date</p>
                    <p className="text-sm font-medium text-slate-900">
                      {ticket.pickup_date ? format(new Date(ticket.pickup_date), 'MMM dd, yyyy') : 'N/A'}
                    </p>
                  </div>
                  {ticket.delivery_date && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Delivery Date</p>
                      <p className="text-sm font-medium text-slate-900">
                        {format(new Date(ticket.delivery_date), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-slate-500 mb-1">Total</p>
                      <p className="text-2xl font-bold text-indigo-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        ₹{(ticket.total_amount || ticket.total_price || 0).toFixed(2)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDeleteTicket(ticket.id, e)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-ticket-${ticket.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const renderDeletedTickets = () => {
    if (deletedTickets.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Trash2 className="w-20 h-20 text-slate-400 mb-4" />
            <p className="text-slate-500 text-lg mb-2">No deleted tickets</p>
            <p className="text-slate-400 text-sm">Deleted tickets will appear here</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {deletedTickets.map((ticket) => (
          <Card
            key={ticket.id}
            className="card-hover opacity-75"
            data-testid={`deleted-ticket-card-${ticket.id}`}
          >
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-mono text-lg font-semibold text-slate-900">
                      {ticket.ticket_number}
                    </h3>
                    <Badge className="bg-red-100 text-red-700">
                      Deleted
                    </Badge>
                    <Badge className={`${getStatusColor(ticket.status)}`}>
                      {ticket.status}
                    </Badge>
                  </div>
                  <p className="text-slate-600 mb-1">{ticket.customer_name}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                    <span>Service: {ticket.service_type}</span>
                    <span>•</span>
                    <span>{ticket.total_kg || ticket.kg || 0} kg</span>
                    <span>•</span>
                    <span>{ticket.clothes_count || ticket.items?.length || 0} items</span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Pickup Date</p>
                    <p className="text-sm font-medium text-slate-900">
                      {ticket.pickup_date ? format(new Date(ticket.pickup_date), 'MMM dd, yyyy') : 'N/A'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-slate-500 mb-1">Total</p>
                      <p className="text-2xl font-bold text-slate-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        ₹{(ticket.total_amount || ticket.total_price || 0).toFixed(2)}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => handleRestoreTicket(ticket.id, e)}
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      data-testid={`restore-ticket-${ticket.id}`}
                    >
                      <RotateCcw className="w-5 h-5 mr-1" />
                      Restore
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Tabs for Active/Deleted */}
      <div className="flex items-center gap-4 border-b border-slate-200 pb-4">
        <button
          onClick={() => setShowDeleted(false)}
          className={`px-4 py-2 font-medium transition-colors ${
            !showDeleted
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="active-tickets-tab"
        >
          Active Tickets ({tickets.length})
        </button>
        <button
          onClick={() => setShowDeleted(true)}
          className={`px-4 py-2 font-medium transition-colors ${
            showDeleted
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="deleted-tickets-tab"
        >
          Deleted Tickets ({deletedTickets.length})
        </button>
      </div>

      {/* Header & Filters - Only show for active tickets */}
      {!showDeleted && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                placeholder="Search by ticket number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-tickets-input"
              />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="flex flex-col">
                <label className="text-xs text-slate-500 mb-1">Pickup Date</label>
                <Input
                  type="date"
                  value={pickupDateFilter}
                  onChange={(e) => setPickupDateFilter(e.target.value)}
                  className="w-full sm:w-[160px]"
                  data-testid="pickup-date-filter-input"
                />
              </div>
              {pickupDateFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPickupDateFilter('')}
                  className="text-slate-500 hover:text-slate-700 mt-5"
                  data-testid="clear-pickup-date-filter-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="flex flex-col">
                <label className="text-xs text-slate-500 mb-1">Delivery Date</label>
                <Input
                  type="date"
                  value={deliveryDateFilter}
                  onChange={(e) => setDeliveryDateFilter(e.target.value)}
                  className="w-full sm:w-[160px]"
                  data-testid="delivery-date-filter-input"
                />
              </div>
              {deliveryDateFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeliveryDateFilter('')}
                  className="text-slate-500 hover:text-slate-700 mt-5"
                  data-testid="clear-delivery-date-filter-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="picked">Picked</SelectItem>
                <SelectItem value="washed">Washed</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => navigate('/tickets/create')}
            data-testid="create-ticket-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Ticket
          </Button>
        </div>
      )}

      {/* Tickets List */}
      {showDeleted ? renderDeletedTickets() : renderActiveTickets()}
    </div>
  );
};

export default Tickets;
